import { Vector2 } from "three";
import { Vector3 } from "three";
import { Vector4 } from "three";
import { Color } from "three";
import { CoreGeometry } from "./Geometry";
import { Attribute, CoreAttribute } from "./Attribute";
import { CoreConstant, AttribType } from "./Constant";
import { CoreMaterial } from "./Material";
import { CoreString } from "../String";
import { CoreEntity } from "./Entity";
import { CoreType } from "../Type";
import { ObjectUtils } from "../ObjectUtils";
import { ArrayUtils } from "../ArrayUtils";
import { ThreeMeshBVHHelper } from "../../engine/operations/sop/utils/Bvh/ThreeMeshBVHHelper";
const NAME_ATTR = "name";
const ATTRIBUTES = "attributes";
const ATTRIBUTES_PREVIOUS_VALUES = "attributesPreviousValues";
const _CoreObject = class extends CoreEntity {
  constructor(_object, index) {
    super(index);
    this._object = _object;
  }
  dispose() {
  }
  object() {
    return this._object;
  }
  geometry() {
    return this._object.geometry;
  }
  coreGeometry() {
    const geo = this.geometry();
    if (geo) {
      return new CoreGeometry(geo);
    } else {
      return null;
    }
  }
  points() {
    var _a;
    return ((_a = this.coreGeometry()) == null ? void 0 : _a.points()) || [];
  }
  pointsFromGroup(group) {
    if (group) {
      const indices = CoreString.indices(group);
      if (indices) {
        const points = this.points();
        return ArrayUtils.compact(indices.map((i) => points[i]));
      } else {
        return [];
      }
    } else {
      return this.points();
    }
  }
  static isInGroup(groupString, coreObject) {
    const group = groupString.trim();
    if (group.length == 0) {
      return true;
    }
    if (coreObject.object.name == group) {
      return true;
    }
    if (CoreString.matchMask(groupString, coreObject.name())) {
      return true;
    }
    const elements = group.split("=");
    const attribNameWithPrefix = elements[0];
    if (attribNameWithPrefix[0] == "@") {
      const attribName = attribNameWithPrefix.substring(1);
      const expectedAttribValue = elements[1];
      const currentAttribValue = coreObject.attribValue(attribName);
      return expectedAttribValue == currentAttribValue;
    }
    return false;
  }
  computeVertexNormals() {
    var _a;
    (_a = this.coreGeometry()) == null ? void 0 : _a.computeVertexNormals();
  }
  static _convert_array_to_vector(value) {
    switch (value.length) {
      case 1:
        return value[0];
      case 2:
        return new Vector2(value[0], value[1]);
      case 3:
        return new Vector3(value[0], value[1], value[2]);
      case 4:
        return new Vector4(value[0], value[1], value[2], value[3]);
    }
  }
  static addAttribute(object, attribName, value) {
    if (CoreType.isArray(value)) {
      const converted_value = this._convert_array_to_vector(value);
      if (!converted_value) {
        const message = `attribute_value invalid`;
        console.error(message, value);
        throw new Error(message);
      }
    }
    this.attributesDictionary(object)[attribName] = value;
  }
  addAttribute(name, value) {
    _CoreObject.addAttribute(this._object, name, value);
  }
  addNumericAttrib(name, value) {
    this.addAttribute(name, value);
  }
  setAttribValue(name, value) {
    this.addAttribute(name, value);
  }
  addNumericVertexAttrib(name, size, defaultValue) {
    var _a;
    if (defaultValue == null) {
      defaultValue = CoreAttribute.default_value(size);
    }
    (_a = this.coreGeometry()) == null ? void 0 : _a.addNumericAttrib(name, size, defaultValue);
  }
  static attributesDictionary(object) {
    return object.userData[ATTRIBUTES] || this._createAttributesDictionaryIfNone(object);
  }
  static attributesPreviousValuesDictionary(object) {
    return object.userData[ATTRIBUTES_PREVIOUS_VALUES] || this._createAttributesPreviousValuesDictionaryIfNone(object);
  }
  static _createAttributesDictionaryIfNone(object) {
    if (!object.userData[ATTRIBUTES]) {
      return object.userData[ATTRIBUTES] = {};
    }
  }
  static _createAttributesPreviousValuesDictionaryIfNone(object) {
    if (!object.userData[ATTRIBUTES_PREVIOUS_VALUES]) {
      return object.userData[ATTRIBUTES_PREVIOUS_VALUES] = {};
    }
  }
  _attributesDictionary() {
    return _CoreObject.attributesDictionary(this._object);
  }
  attributeNames() {
    return Object.keys(this._attributesDictionary());
  }
  attribNames() {
    return this.attributeNames();
  }
  hasAttrib(name) {
    return this.attributeNames().includes(name);
  }
  renameAttrib(old_name, new_name) {
    const current_value = this.attribValue(old_name);
    if (current_value != null) {
      this.addAttribute(new_name, current_value);
      this.deleteAttribute(old_name);
    } else {
      console.warn(`attribute ${old_name} not found`);
    }
  }
  deleteAttribute(name) {
    delete this._attributesDictionary()[name];
  }
  static deleteAttribute(object, attribName) {
    delete this.attributesDictionary(object)[attribName];
  }
  static attribValue(object, attribName, index = 0, target) {
    if (attribName === Attribute.OBJECT_INDEX) {
      return index;
    }
    if (object.userData) {
      const dict = this.attributesDictionary(object);
      const val = dict[attribName];
      if (val == null) {
        if (attribName == NAME_ATTR) {
          return object.name;
        }
      } else {
        if (CoreType.isArray(val) && target) {
          target.fromArray(val);
          return target;
        }
      }
      return val;
    }
    if (attribName == NAME_ATTR) {
      return object.name;
    }
  }
  static previousAttribValue(object, attribName) {
    const dict = this.attributesPreviousValuesDictionary(object);
    return dict[attribName];
  }
  static stringAttribValue(object, attribName, index = 0) {
    const str = this.attribValue(object, attribName, index);
    if (str != null) {
      if (CoreType.isString(str)) {
        return str;
      } else {
        return `${str}`;
      }
    }
  }
  static makeAttribReactive(object, attribName, callback) {
    const attributesDict = this.attributesDictionary(object);
    const attributesPreviousValuesDict = this.attributesPreviousValuesDictionary(object);
    if (attributesDict[attribName] == null) {
      attributesDict[attribName] = 0;
    }
    const proxy = {
      value: attributesDict[attribName],
      previousValue: attributesDict[attribName]
    };
    Object.defineProperties(attributesDict, {
      [attribName]: {
        get: function() {
          return proxy.value;
        },
        set: function(x) {
          if (x != proxy.value) {
            proxy.previousValue = proxy.value;
            proxy.value = x;
            callback(proxy.value, proxy.previousValue);
          }
          return proxy.value;
        },
        configurable: true
      }
    });
    Object.defineProperties(attributesPreviousValuesDict, {
      [attribName]: {
        get: function() {
          return proxy.previousValue;
        },
        configurable: true
      }
    });
  }
  attribValue(attribName, target) {
    return _CoreObject.attribValue(this._object, attribName, this._index, target);
  }
  stringAttribValue(name) {
    return _CoreObject.stringAttribValue(this._object, name, this._index);
  }
  name() {
    return this.attribValue(NAME_ATTR);
  }
  humanType() {
    return CoreConstant.CONSTRUCTOR_NAMES_BY_CONSTRUCTOR_NAME[this._object.constructor.name];
  }
  attribTypes() {
    const h = {};
    for (let attrib_name of this.attribNames()) {
      const type = this.attribType(attrib_name);
      if (type != null) {
        h[attrib_name] = type;
      }
    }
    return h;
  }
  attribType(name) {
    const val = this.attribValue(name);
    if (CoreType.isString(val)) {
      return AttribType.STRING;
    } else {
      return AttribType.NUMERIC;
    }
  }
  attribSizes() {
    const h = {};
    for (let attrib_name of this.attribNames()) {
      const size = this.attribSize(attrib_name);
      if (size != null) {
        h[attrib_name] = size;
      }
    }
    return h;
  }
  attribSize(name) {
    const val = this.attribValue(name);
    if (val == null) {
      return null;
    }
    return CoreAttribute.attribSizeFromValue(val);
  }
  clone() {
    return _CoreObject.clone(this._object);
  }
  static clone(srcObject) {
    const clonedObject = srcObject.clone();
    var sourceLookup = /* @__PURE__ */ new Map();
    var cloneLookup = /* @__PURE__ */ new Map();
    _CoreObject.parallelTraverse(srcObject, clonedObject, function(sourceNode, clonedNode) {
      sourceLookup.set(clonedNode, sourceNode);
      cloneLookup.set(sourceNode, clonedNode);
    });
    clonedObject.traverse(function(node) {
      const srcNode = sourceLookup.get(node);
      const meshNode = node;
      if (meshNode.geometry && srcNode && srcNode.geometry) {
        const srcNodeGeometry = srcNode.geometry;
        meshNode.geometry = CoreGeometry.clone(srcNodeGeometry);
        ThreeMeshBVHHelper.copyBVH(meshNode, srcNode);
      }
      if (meshNode.material) {
        CoreMaterial.applyCustomMaterials(node, meshNode.material);
        const material_with_color = meshNode.material;
        if (material_with_color.color == null) {
          material_with_color.color = new Color(1, 1, 1);
        }
      }
      if (srcNode) {
        if (srcNode.userData) {
          node.userData = ObjectUtils.cloneDeep(srcNode.userData);
        }
        const src_node_with_animations = srcNode;
        if (src_node_with_animations.animations) {
          node.animations = src_node_with_animations.animations.map((animation) => animation.clone());
        }
        const skinned_node = node;
        if (skinned_node.isSkinnedMesh) {
          var clonedMesh = skinned_node;
          var sourceMesh = srcNode;
          var sourceBones = sourceMesh.skeleton.bones;
          clonedMesh.skeleton = sourceMesh.skeleton.clone();
          clonedMesh.bindMatrix.copy(sourceMesh.bindMatrix);
          const new_bones = sourceBones.map(function(bone) {
            return cloneLookup.get(bone);
          });
          clonedMesh.skeleton.bones = new_bones;
          clonedMesh.bind(clonedMesh.skeleton, clonedMesh.bindMatrix);
        }
      }
    });
    return clonedObject;
  }
  static parallelTraverse(a, b, callback) {
    callback(a, b);
    for (var i = 0; i < a.children.length; i++) {
      const childA = a.children[i];
      const childB = b.children[i];
      if (childA && childB) {
        this.parallelTraverse(childA, childB, callback);
      }
    }
  }
};
export let CoreObject = _CoreObject;
CoreObject.setAttribute = _CoreObject.addAttribute;
